import { Box, Stack } from '@mui/joy';
import { ReactNode, useState } from 'react';

import { InfiniteScroll } from 'core/components';

const LIST_HEIGHT = 'fit-content';

interface IProps<T extends { id: number }> {
  records: T[];
  hasNextPage: boolean;
  fetchNextPage: () => void;
  getItemKey?: (item: T) => string;
  renderItem: (item: T, onSelectItemClick: (item: T) => void) => ReactNode;
  renderDetails?: (
    isDetailsOpen: boolean,
    onCloseDetails: () => void,
    selectedItem: T | null
  ) => ReactNode;
}

const defaultGetItemKey = (item: { id: number }) => String(item.id);

export const List = <T extends { id: number }>({
  records,
  hasNextPage,
  fetchNextPage,
  getItemKey = defaultGetItemKey,
  renderItem,
  renderDetails,
}: IProps<T>) => {
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [selectedItem, setSelectedItem] = useState<T | null>(null);

  const onSelectItemClick = (item: T) => {
    setSelectedItem(item);
    setIsDetailsOpen(true);
  };

  const onCloseDetails = () => {
    setIsDetailsOpen(false);
    setSelectedItem(null);
  };

  return (
    <>
      <InfiniteScroll
        data={records}
        fetchNextPage={fetchNextPage}
        hasNextPage={hasNextPage}
        height={LIST_HEIGHT}
        style={{
          overflowX: 'hidden',
          maxHeight: 'calc(100vh - 228px)',
          alignItems: 'center',
        }}
      >
        <Stack gap={1}>
          {records.map((record) => (
            <Box key={getItemKey(record)}>{renderItem(record, onSelectItemClick)}</Box>
          ))}
        </Stack>
      </InfiniteScroll>

      {renderDetails && renderDetails(isDetailsOpen, onCloseDetails, selectedItem)}
    </>
  );
};
